import React from 'react';

import { Resource } from '../../../../common/constants/resource';
import { MVRDriverRecord, MVRViolations } from '../../../tasks/types/mvr';
import { dateToString } from '../../utilities/formattingUtilities';
import styles from './mvr.module.scss';
import {
  categorizeDisposition,
  summarizeDispositions,
  sumPoints,
} from './mvrHelpers';

const renderSectionHeader = (meta: string) => (
  <div className={styles.sectionHeader}>
    <span className={styles.sectionTitle}>
      {Resource.MVR_DRIVERS_RECORD_SUBTITLE}
    </span>
    <span className={styles.sectionMeta}>{meta}</span>
  </div>
);

const renderSummaryChips = (violations: MVRViolations[]) => {
  const summaries = summarizeDispositions(violations);
  const totalPoints = sumPoints(violations);
  return (
    <div className={styles.summaryChips}>
      {summaries.map(summary => {
        const dotClass =
          summary.category === 'guilty' ||
          summary.category === 'dismissed' ||
          summary.category === 'admin'
            ? `${styles.chipDot} ${styles[summary.category]}`
            : undefined;
        return (
          <span
            className={`${styles.chip} ${styles[summary.category] || ''}`}
            key={summary.label}
          >
            {dotClass && <span className={dotClass} />}
            {`${summary.count} ${summary.label}`}
          </span>
        );
      })}
      {totalPoints > 0 && (
        <span className={`${styles.chip} ${styles.points}`}>
          {`${totalPoints} ${Resource.MVR_DRIVERS_RECORD_POINTS}`}
        </span>
      )}
    </div>
  );
};

const renderTableHeader = () => (
  <div className={styles.vTableHeader}>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_VS_DATE}</div>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_DESCRIPTION}</div>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_VCODE}</div>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_CLASS}</div>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_STATE_VCODE}</div>
    <div className={styles.vth}>{Resource.MVR_DRIVERS_RECORD_POINTS}</div>
    <div className={styles.vth}>{Resource.MVR_VIOLATION_DISPOSITION}</div>
  </div>
);

const renderViolationRow = (violation: MVRViolations, key: number) => {
  const category = categorizeDisposition(violation.disposition);
  const rowClass =
    category === 'dismissed'
      ? `${styles.violationRow} ${styles.dismissedRow}`
      : styles.violationRow;
  const dispClass = `${styles.dispPill} ${styles[category] || ''}`;
  const hasEvc = violation.providerCode || violation.violationCategory;

  return (
    <div className={rowClass} key={key}>
      <div className={styles.violationMain}>
        <div className={styles.vcell}>
          <div className={styles.vDate}>
            <div className={styles.vDateMain}>
              {dateToString(violation.suspensionDate)}
            </div>
            {violation.reinstatementDate && (
              <div className={styles.vDateSub}>
                {`${Resource.MVR_CR_LABEL}: ${violation.reinstatementDate}`}
              </div>
            )}
          </div>
        </div>
        <div className={styles.vcell}>
          <div>
            <div className={styles.vDesc}>
              {violation.description || Resource.NORA_M_DASH}
            </div>
            {hasEvc && (
              <div className={styles.evcSub}>
                {violation.providerCode && (
                  <span className={styles.evcCodeInline}>
                    {violation.providerCode}
                  </span>
                )}
                {violation.violationCategory && (
                  <span className={styles.evcDescInline}>
                    {violation.violationCategory}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.vcell}>
          {violation.standardViolationCode ? (
            <span className={styles.vCode}>
              {violation.standardViolationCode}
            </span>
          ) : (
            <span className={`${styles.vCode} ${styles.empty}`}>
              {Resource.NORA_M_DASH}
            </span>
          )}
        </div>
        <div className={styles.vcell}>
          {violation.violationClass || Resource.NORA_M_DASH}
        </div>
        <div className={styles.vcell}>
          {violation.stateViolationCode || Resource.NORA_M_DASH}
        </div>
        <div className={styles.vcell}>
          {violation.points != null ? violation.points : Resource.NORA_M_DASH}
        </div>
        <div className={styles.vcell}>
          {violation.disposition ? (
            <span className={dispClass}>{violation.disposition}</span>
          ) : (
            Resource.NORA_M_DASH
          )}
        </div>
      </div>
    </div>
  );
};

/** Redesigned drivers-record section: header, summary chips and violations table. */
export const MVRViolationsTable = ({
  driverRecord,
}: {
  driverRecord: MVRDriverRecord;
}) => {
  if (
    driverRecord.violationStatus &&
    driverRecord.violationStatus.toUpperCase() === Resource.MVR_STATUS_CLEAR
  ) {
    return (
      <>
        {renderSectionHeader(Resource.MVR_STATUS_CLEAR)}
        <div className={styles.infoBar}>
          <div className={styles.infoField}>
            <div className={styles.fieldLabel}>
              {Resource.MVR_CLASSIFICATION}
            </div>
            <div className={styles.fieldValue}>
              {driverRecord.classification || Resource.NORA_M_DASH}
            </div>
          </div>
          <div className={styles.infoField}>
            <div className={styles.fieldLabel}>{Resource.MVR_DESCRIPTION}</div>
            <div className={styles.fieldValue}>
              {driverRecord.description || Resource.NORA_M_DASH}
            </div>
          </div>
        </div>
      </>
    );
  }

  const violations = driverRecord.violations;
  if (!violations || violations.length === 0) {
    if (
      driverRecord.violationStatus?.toUpperCase() === Resource.MVR_STATUS_HIT
    ) {
      return (
        <>
          {renderSectionHeader(Resource.NORA_M_DASH)}
          <div className={styles.miscBody}>
            {Resource.MVR_NO_RATABLE_VIOLATIONS}
          </div>
        </>
      );
    }
    return <></>;
  }

  const meta = `${violations.length} ${
    violations.length === 1
      ? Resource.MVR_VIOLATION_SUFFIX
      : Resource.MVR_VIOLATIONS_SUFFIX
  }`;

  return (
    <>
      {renderSectionHeader(meta)}
      {renderSummaryChips(violations)}
      <div className={styles.violationsScroll}>
        {renderTableHeader()}
        {violations.map((violation, idx) =>
          renderViolationRow(violation, idx),
        )}
      </div>
    </>
  );
};
